import { Button, Card, Empty, Space, Spin, Tag, Timeline, Tooltip, Typography } from "antd";
import {
  HistoryOutlined,
  LinkOutlined,
  UserOutlined,
  EnvironmentOutlined,
  AlertOutlined,
  ExperimentOutlined,
  BlockOutlined,
  DownOutlined,
  WarningOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { apiClient } from "../../../services/api/apiClient";

const { Text } = Typography;

interface NamedRef {
  id: number | string;
  name: string;
  /** Optional surface in hectares. When provided, the timeline shows
   *  "<name> · <surface> ha" so the user can compare audits at a
   *  glance without opening each one. */
  surface?: number | null;
  /** Optional audited production in kilograms (only meaningful for
   *  AuditPlot entries — the auditor's `kg_plot` for that plot in
   *  that audit). Rendered after surface when present. */
  kg_plot?: number | null;
}

interface IncidenceRef {
  id: number;
  num_incidence: string;
  subject: string;
  kind_name: string;
  /** 1=Open, 2=Closed, 3=Cancelled (matches INCIDENCE_STATUS choices). */
  status: number;
}

interface FarmRename {
  id: string;
  previous_name: string;
  new_name: string;
  created: string | null;
}

interface FarmHistoryEntry {
  id: number;
  reference: string;
  start_date: string | null;
  planning_date: string | null;
  auditor_name: string | null;
  audit_state_name: string | null;
  audit_type_name: string | null;
  plots_created: NamedRef[];
  control_points_created: NamedRef[];
  adjacent_crops_created: NamedRef[];
  farm_renames: FarmRename[];
  incidences: IncidenceRef[];
  samples_count: number;
  is_current: boolean;
}

interface FarmHistoryResponse {
  audits: FarmHistoryEntry[];
}

interface FarmHistoryCardProps {
  auditId: number;
  farmName?: string;
  /** Bumping this triggers a re-fetch of the history. Used by the
   *  parent after the current audit gets finalised so the row that
   *  represents this audit picks up the new state ("Finalizada"). */
  refreshKey?: number;
}

/**
 * Timeline of every audit performed on the same farm as the current
 * audit. Each entry shows the items "created" during that audit
 * (plots, control points, adjacent crops) plus a sample count. The
 * audit reference is a link that opens the audit in a new tab.
 */
// Initial number of audits shown before "Cargar más". Kept small so
// the card doesn't grow long when a farm has dozens of audits.
const INITIAL_LIMIT = 3;
const PAGE_SIZE = 5;

export const FarmHistoryCard = ({
  auditId,
  farmName,
  refreshKey = 0,
}: FarmHistoryCardProps) => {
  const { t } = useTranslation();
  const [data, setData] = useState<FarmHistoryEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(INITIAL_LIMIT);

  useEffect(() => {
    if (!auditId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apiClient.get<FarmHistoryResponse>(
          `/audits/${auditId}/farm_history/`,
        );
        if (!cancelled) setData(res.data.audits ?? []);
      } catch {
        if (!cancelled) setData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `refreshKey` participates so a parent bump (e.g. after the
    // current audit is finalised) re-fetches the timeline and the
    // row for this audit picks up its new state.
  }, [auditId, refreshKey]);

  const fmtDate = (s: string | null): string =>
    s ? dayjs(s).format("DD/MM/YYYY") : t("audit.no_date", "Sin fecha");

  const fmtKg = (n: number) =>
    new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(n);

  const renderNamedList = (items: NamedRef[], max = 8): string => {
    if (items.length === 0) return "";
    // Build "Name · 1.23 ha · 12.345 kg" entries when surface /
    // kg_plot are provided, falling back to fewer segments
    // otherwise. Filtering by .name keeps the legacy entries (no
    // name) out of the list.
    const labels = items
      .filter((i) => i.name)
      .map((i) => {
        const parts = [i.name];
        if (i.surface != null && i.surface > 0) {
          parts.push(`${i.surface.toFixed(2)} ha`);
        }
        if (i.kg_plot != null && i.kg_plot > 0) {
          parts.push(`${fmtKg(i.kg_plot)} kg`);
        }
        return parts.join(" · ");
      });
    if (labels.length <= max) return labels.join(", ");
    return `${labels.slice(0, max).join(", ")} … (+${labels.length - max})`;
  };

  const renderEntry = (entry: FarmHistoryEntry) => {
    const hasNothing =
      entry.plots_created.length === 0 &&
      entry.control_points_created.length === 0 &&
      entry.adjacent_crops_created.length === 0 &&
      entry.incidences.length === 0 &&
      (entry.farm_renames?.length ?? 0) === 0 &&
      entry.samples_count === 0;

    return (
      <div style={{ paddingBottom: 4 }}>
        {/* Header line: reference (link) + date + state + current tag */}
        <Space size={8} wrap style={{ marginBottom: 4 }}>
          <a
            href={`/audits/${entry.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontWeight: 600 }}
          >
            {entry.reference}
            <LinkOutlined style={{ marginLeft: 4, fontSize: 11 }} />
          </a>
          <Text type="secondary">{fmtDate(entry.start_date)}</Text>
          {entry.audit_type_name && (
            <Tag color="geekblue">{entry.audit_type_name}</Tag>
          )}
          {entry.audit_state_name && (
            <Tag color={entry.is_current ? "blue" : "default"}>
              {entry.audit_state_name}
            </Tag>
          )}
          {entry.is_current && (
            <Tag color="processing">
              {t("audit.history.current", "Actual")}
            </Tag>
          )}
        </Space>

        {/* Auditor */}
        {entry.auditor_name && (
          <div style={{ marginBottom: 4, color: "#595959", fontSize: 13 }}>
            <UserOutlined style={{ marginRight: 6 }} />
            {entry.auditor_name}
          </div>
        )}

        {/* Items created lines */}
        {entry.plots_created.length > 0 && (
          <div style={{ fontSize: 13, color: "#262626" }}>
            <BlockOutlined style={{ marginRight: 6, color: "#ff8c00" }} />
            <Text strong>
              {t("audit.history.plots", "Parcelas")} ({entry.plots_created.length}):
            </Text>{" "}
            <Tooltip title={renderNamedList(entry.plots_created, 999)}>
              <Text>{renderNamedList(entry.plots_created)}</Text>
            </Tooltip>
          </div>
        )}
        {entry.control_points_created.length > 0 && (
          <div style={{ fontSize: 13, color: "#262626" }}>
            <AlertOutlined style={{ marginRight: 6, color: "#ff4d4f" }} />
            <Text strong>
              {t("audit.history.cps", "Puntos de control")} (
              {entry.control_points_created.length}):
            </Text>{" "}
            <Tooltip title={renderNamedList(entry.control_points_created, 999)}>
              <Text>{renderNamedList(entry.control_points_created)}</Text>
            </Tooltip>
          </div>
        )}
        {entry.adjacent_crops_created.length > 0 && (
          <div style={{ fontSize: 13, color: "#262626" }}>
            <EnvironmentOutlined style={{ marginRight: 6, color: "#722ed1" }} />
            <Text strong>
              {t("audit.history.adjacent_crops", "Cultivos colindantes")} (
              {entry.adjacent_crops_created.length}):
            </Text>{" "}
            <Tooltip title={renderNamedList(entry.adjacent_crops_created, 999)}>
              <Text>{renderNamedList(entry.adjacent_crops_created)}</Text>
            </Tooltip>
          </div>
        )}
        {entry.samples_count > 0 && (
          <div style={{ fontSize: 13, color: "#262626" }}>
            <ExperimentOutlined style={{ marginRight: 6, color: "#1677ff" }} />
            <Text strong>
              {t("audit.history.samples", "Muestras tomadas")}:
            </Text>{" "}
            <Text>{entry.samples_count}</Text>
          </div>
        )}
        {entry.farm_renames && entry.farm_renames.length > 0 && (
          <div style={{ fontSize: 13, color: "#262626" }}>
            <EditOutlined style={{ marginRight: 6, color: "#13c2c2" }} />
            <Text strong>
              {t("audit.history.farm_renamed", "Finca renombrada")}:
            </Text>{" "}
            {entry.farm_renames.map((r, i) => (
              <span key={r.id}>
                {i > 0 && " · "}
                <Text type="secondary">{r.previous_name || "—"}</Text>{" → "}
                <Text strong>{r.new_name}</Text>
              </span>
            ))}
          </div>
        )}
        {entry.incidences.length > 0 && (
          <div style={{ fontSize: 13, color: "#262626" }}>
            <WarningOutlined style={{ marginRight: 6, color: "#faad14" }} />
            <Text strong>
              {t("audit.history.incidences", "Incidencias")} (
              {entry.incidences.length}):
            </Text>{" "}
            <Tooltip
              title={entry.incidences
                .map(
                  (i) =>
                    `${i.num_incidence}${i.subject ? ` — ${i.subject}` : ""}${i.kind_name ? ` (${i.kind_name})` : ""}`,
                )
                .join("\n")}
            >
              <Space size={4} wrap>
                {entry.incidences.slice(0, 5).map((i) => (
                  <a
                    key={i.id}
                    href={`/incidences/${i.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Tag
                      color={i.status === 1 ? "gold" : i.status === 2 ? "green" : "default"}
                      style={{ margin: 0, cursor: "pointer" }}
                    >
                      {i.num_incidence}
                    </Tag>
                  </a>
                ))}
                {entry.incidences.length > 5 && (
                  <Text type="secondary">
                    +{entry.incidences.length - 5}
                  </Text>
                )}
              </Space>
            </Tooltip>
          </div>
        )}
        {hasNothing && (
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t(
              "audit.history.nothing",
              "Sin parcelas, puntos de control ni muestras registradas.",
            )}
          </Text>
        )}
      </div>
    );
  };

  return (
    <Card
      title={
        <Space>
          <HistoryOutlined />
          <span>
            {t("audit.history.title", "Histórico de la finca")}
            {farmName ? ` — ${farmName}` : ""}
          </span>
        </Space>
      }
      style={{ marginBottom: 24 }}
    >
      {loading && (
        <div style={{ textAlign: "center", padding: 32 }}>
          <Spin />
        </div>
      )}
      {!loading && data && data.length === 0 && (
        <Empty
          description={t(
            "audit.history.empty",
            "No hay auditorías previas registradas en esta finca.",
          )}
        />
      )}
      {!loading && data && data.length > 0 && (
        <>
          <Timeline
            mode="left"
            items={data.slice(0, limit).map((entry) => ({
              color: entry.is_current ? "blue" : "gray",
              children: renderEntry(entry),
            }))}
          />
          {data.length > limit && (
            <div style={{ textAlign: "center", marginTop: 8 }}>
              <Button
                type="link"
                icon={<DownOutlined />}
                onClick={() => setLimit((n) => n + PAGE_SIZE)}
              >
                {t("audit.history.load_more", "Cargar más")} (
                {data.length - limit})
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default FarmHistoryCard;
